import React, { useEffect, useState } from "react";
import { Check, X, Star } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";

const resolve = (u) => (u && u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
const TABS = ["pending", "approved", "rejected"];

export default function AdminReviews() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");

  const load = (s) => api.get("/admin/reviews", { params: { status: s } }).then((res) => setItems(res.data)).catch(() => {});

  useEffect(() => { load(status); }, [status]);

  const decide = async (id, next) => {
    setBusy(id);
    try {
      const res = await api.patch(`/admin/reviews/${id}`, { status: next });
      toast.success(next === "approved" ? `Approved — code ${res.data.reward_coupon_code || "issued"}` : "Review rejected");
      load(status);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not update that review"));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="admin-reviews-page">
      <h1 className="font-heading font-bold text-2xl text-zinc-900 mb-6">Customer Photos & Reviews</h1>
      <div className="flex gap-2 mb-6">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setStatus(t)}
            className={`px-4 py-2 text-xs font-heading font-bold uppercase tracking-wide rounded-full transition-colors ${status === t ? "bg-charcoal text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
            data-testid={`admin-reviews-tab-${t}`}
          >
            {t}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="admin-reviews-empty">Nothing {status} right now.</p>
      ) : (
        <div className="space-y-4">
          {items.map((r) => (
            <div key={r.id} className="bg-white border border-zinc-200 rounded-xl p-5" data-testid={`admin-review-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="font-heading font-bold text-sm text-zinc-900">{r.brand_name} {r.model}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {r.user_name} · {r.user_email} · order {r.order_number} · {formatDate(r.created_at)}
                  </div>
                  <div className="flex gap-0.5 mt-2">
                    {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={13} className={n <= r.rating ? "fill-amber-400 text-amber-400" : "text-zinc-300"} />)}
                  </div>
                  {r.body && <p className="text-sm text-zinc-700 mt-3 max-w-2xl">{r.body}</p>}
                  {r.reward_coupon_code && <div className="text-xs font-mono font-bold text-teal-700 mt-2">Reward code: {r.reward_coupon_code}</div>}
                </div>
                {status === "pending" && (
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => decide(r.id, "approved")} disabled={busy === r.id} className="inline-flex items-center gap-1.5 bg-teal-600 text-white text-xs font-heading font-bold uppercase tracking-wide px-4 py-2.5 rounded-full hover:bg-teal-700 disabled:opacity-40" data-testid={`admin-review-approve-${r.id}`}>
                      <Check size={13} /> Approve
                    </button>
                    <button onClick={() => decide(r.id, "rejected")} disabled={busy === r.id} className="inline-flex items-center gap-1.5 border border-zinc-300 text-zinc-700 text-xs font-heading font-bold uppercase tracking-wide px-4 py-2.5 rounded-full hover:border-zinc-500 disabled:opacity-40" data-testid={`admin-review-reject-${r.id}`}>
                      <X size={13} /> Reject
                    </button>
                  </div>
                )}
              </div>
              {r.photos?.length > 0 && (
                <div className="flex gap-2 mt-4 flex-wrap">
                  {r.photos.map((p) => (
                    <a key={p} href={resolve(p)} target="_blank" rel="noreferrer">
                      <img src={resolve(p)} alt="Customer submission" className="w-28 h-28 object-cover rounded-lg border border-zinc-200" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
